import { authService } from '../auth/AuthService';
import {IBookmark} from "@/common/types/IBookmark";
import {APP_CONSTANTS} from "@/utilities/constants/AppConstants.ts";

interface SyncBookmark extends Partial<IBookmark> {
  isFavorite: boolean;
  lastModifiedTime: string;
}

interface SyncResponse {
  success: boolean;
  bookmarks?: SyncBookmark[];
  error?: string;
}

interface SyncStatus {
  lastSync: number;
  pendingChanges: number;
  isOnline: boolean;
}

class SyncService {
  private readonly API_BASE_URL = APP_CONSTANTS.API_BASE_URL;
  private readonly SYNC_STATUS_KEY = 'bookmarker_sync_status';
  private readonly PENDING_CHANGES_KEY = 'bookmarker_pending_changes';
  private syncInProgress = false;

  // Get sync status
  getSyncStatus(): SyncStatus {
    const statusStr = localStorage.getItem(this.SYNC_STATUS_KEY);
    return statusStr ? JSON.parse(statusStr) : {
      lastSync: 0,
      pendingChanges: 0,
      isOnline: navigator.onLine
    };
  }

  // Update sync status
  private updateSyncStatus(status: Partial<SyncStatus>): void {
    const currentStatus = this.getSyncStatus();
    const newStatus = { ...currentStatus, ...status };
    localStorage.setItem(this.SYNC_STATUS_KEY, JSON.stringify(newStatus));
  }

  // Get pending changes
  getPendingChanges(): SyncBookmark[] {
    const changesStr = localStorage.getItem(this.PENDING_CHANGES_KEY);
    return changesStr ? JSON.parse(changesStr) : [];
  }

  // Add pending change
  private addPendingChange(bookmark: SyncBookmark, action: 'create' | 'update' | 'delete'): void {
    const pending = this.getPendingChanges();
    const change = {
      ...bookmark,
      _syncAction: action,
      _syncTimestamp: Date.now()
    };

    // Remove existing change for this bookmark if exists
    const filtered = pending.filter(p => p.id !== bookmark.id);
    filtered.push(change);

    localStorage.setItem(this.PENDING_CHANGES_KEY, JSON.stringify(filtered));
    this.updateSyncStatus({ pendingChanges: filtered.length });
  }

  // Clear pending changes
  // private clearPendingChanges(): void {
  //   localStorage.removeItem(this.PENDING_CHANGES_KEY);
  //   this.updateSyncStatus({ pendingChanges: 0 });
  // }

  // Check if user is authenticated
  private isAuthenticated(): boolean {
    return authService.isAuthenticated();
  }

  // Get auth headers
  private getAuthHeaders(): Record<string, string> {
    return authService.getAuthHeaders();
  }

  // Sync bookmarks to backend
  async syncToBackend(localBookmarks: SyncBookmark[], forceSync?: boolean): Promise<SyncResponse> {
    if (!this.isAuthenticated()) {
      return { success: false, error: 'User not authenticated' };
    }

    if (this.syncInProgress) {
      return { success: false, error: 'Sync already in progress' };
    }

    if (!forceSync) {
      // Only sync if there are pending changes or if sync is needed
      // if (!this.isSyncNeeded()) return { success: false, error: 'Sync not required' };
    }


    this.syncInProgress = true;

    try {
      // First, sync pending changes
      // await this.syncPendingChanges();

      // Then, sync all bookmarks
      const response = await fetch(`${this.API_BASE_URL}/bookmarks/sync`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          bookmarks: localBookmarks.map(b => this.convertLocalBookmarkToSync(b)),
          deviceId: 'web-app' // Add deviceId as required by backend
        }),
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const result = await response.json();

      // Update sync status
      this.updateSyncStatus({
        lastSync: Date.now(),
        isOnline: true
      });
      return { success: true, bookmarks: result.bookmarks };
    } catch (error) {
      console.error('Sync to backend error:', error);
      this.updateSyncStatus({ isOnline: false });
      return { success: false, error: error instanceof Error ? error.message : 'Sync failed' };
    } finally {
      this.syncInProgress = false;
    }
  }

  // Sync pending changes
  // private async syncPendingChanges(): Promise<void> {
  //   const pending = this.getPendingChanges();
  //   if (pending.length === 0) return;
  //
  //   try {
  //     const response = await fetch(`${this.API_BASE_URL}/bookmarks/batch`, {
  //       method: 'POST',
  //       headers: this.getAuthHeaders(),
  //       body: JSON.stringify({
  //         changes: pending,
  //         deviceId: 'web-app' // Add deviceId as required by backend
  //       }),
  //     });
  //
  //     if (response.ok) {
  //       this.clearPendingChanges();
  //     }
  //   } catch (error) {
  //     console.error('Sync pending changes error:', error);
  //   }
  // }

  // Fetch bookmarks from backend
  async fetchFromBackend(): Promise<SyncResponse> {
    if (!this.isAuthenticated()) {
      return { success: false, error: 'User not authenticated' };
    }

    try {
      const response = await fetch(`${this.API_BASE_URL}/bookmarks`, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Fetch failed: ${response.statusText}`);
      }

      const result = await response.json();

      this.updateSyncStatus({
        lastSync: Date.now(),
        isOnline: true
      });

      return { success: true, bookmarks: result.bookmarks };
    } catch (error) {
      console.error('Fetch from backend error:', error);
      this.updateSyncStatus({ isOnline: false });
      return { success: false, error: error instanceof Error ? error.message : 'Fetch failed' };
    }
  }

  // Add bookmark for sync
  addBookmark(bookmark: SyncBookmark): void {
    this.addPendingChange(bookmark, 'create');
  }

  // Update bookmark for sync
  updateBookmark(bookmark: SyncBookmark): void {
    this.addPendingChange(bookmark, 'update');
  }

  // Delete bookmark for sync
  deleteBookmark(bookmark: SyncBookmark): void {
    this.addPendingChange(bookmark, 'delete');
  }

  // Check if sync is needed
  isSyncNeeded(): boolean {
    const status = this.getSyncStatus();
    return status.pendingChanges > 0 || !status.isOnline || status.lastSync - Date.now() > 10 * 60 * 1000; // 10 minutes
  }

  // Get sync status for UI
  getSyncInfo(): { status: string; lastSync: string; pendingChanges: number } {
    const syncStatus = this.getSyncStatus();

    let status = 'synced';
    if (syncStatus.pendingChanges > 0) {
      status = 'pending';
    } else if (!syncStatus.isOnline) {
      status = 'offline';
    }

    const lastSync = syncStatus.lastSync > 0
      ? new Date(syncStatus.lastSync).toLocaleString()
      : 'Never';

    return {
      status,
      lastSync,
      pendingChanges: syncStatus.pendingChanges
    };
  }

  // Convert local bookmark to sync format
  private convertLocalBookmarkToSync(bookmark: any): SyncBookmark {
    // Safely handle date conversion
    let lastModifiedTime: string;
    try {
      const timestamp = bookmark.updatedAt || bookmark.addedOn;
      if (timestamp && !isNaN(timestamp) && timestamp > 0) {
        lastModifiedTime = new Date(timestamp).toISOString();
      } else {
        // Fallback to current time if invalid timestamp
        lastModifiedTime = new Date().toISOString();
      }
    } catch (error) {
      console.warn('Error converting timestamp, using current time:', error);
      lastModifiedTime = new Date().toISOString();
    }

    return {
      // id: bookmark.id,
      url: bookmark.url,
      title: bookmark.title,
      // type: bookmark.type,
      source: bookmark.source || '',
      image: bookmark.image || '',
      description: bookmark.description,
      tags: bookmark.tags?.map((tag: any) => tag.name) || [],
      categories: bookmark.categories || [],
      isFavorite: bookmark.is_starred === 'true',
      // isArchived: false, // Not implemented in current schema
      // isDeleted: false,
      lastModifiedTime,
    };
  }
}

export const syncService = new SyncService();
export type { SyncBookmark, SyncResponse, SyncStatus };
